import { EXTERNAL_SUBMODULE_TYPES, SubmoduleCategory } from '@constants/constants';
import {
  Module,
  Submodule,
  SubmoduleFilterMap,
  SubmoduleFilter,
} from '@stores/Modules/types/modules';
import { BasicGameApiResponse } from '@api/types/games';
import { BasicCompanyDetails } from '@api/types/pvxPartners';
import { FilterListItem, FilterListItemMap } from '../components/AppAccessSidepanel/types';
import { Company } from '@stores/CompanyList/types/companyList';
import {
  AppAccessFilter,
  AppAccessType,
  DashboardSelectorModuleItem,
  DashboardSelectorSubmoduleItem,
} from '../types/dashboardSelector';
import { SubmoduleFilterRegistry } from '../hooks/useSubmoduleFilterRegistry';
import { UserRole } from '../types/userDetailsForm';
import { SubmoduleScopeType, UserSubmoduleMap } from '@api/company';

export const buildListableDashboardModules = (
  modules: Module[],
  includePermissionSubmodules?: boolean,
  allowedSubmoduleTypes?: string[],
): DashboardSelectorModuleItem[] => {
  const isListable = (subModule: Submodule) => {
    const type = subModule.submoduleType || '';
    if (EXTERNAL_SUBMODULE_TYPES.includes(type)) return false;
    if (allowedSubmoduleTypes && !allowedSubmoduleTypes.includes(type)) return false;
    if (
      !includePermissionSubmodules &&
      subModule.submoduleCategory === SubmoduleCategory.PERMISSION
    ) {
      return false;
    }
    return true;
  };

  const listableDashboardModules: DashboardSelectorModuleItem[] = [];

  for (const module of modules) {
    const listableSubmodules = (module.submodules || []).filter(isListable);

    if (listableSubmodules.length === 0) {
      continue;
    }

    const moduleItem: DashboardSelectorModuleItem = {
      id: module.id,
      name: module.name,
      submodules: [],
    };

    for (const subModule of listableSubmodules) {
      moduleItem.submodules.push({
        name: subModule.name,
        submoduleId: subModule.id,
        moduleId: module.id,
        submoduleType: subModule.submoduleType,
        defaultFilter: subModule.defaultFilter,
        nativeFilterId: subModule.nativeFilterId,
        hasFullAccess: subModule.hasFullAccess,
      });
    }

    listableDashboardModules.push(moduleItem);
  }

  return listableDashboardModules;
};

/** Maps AppAccess filters to the string[] the API expects. */
export const toApiFilterIds = (filters?: AppAccessFilter[]): string[] =>
  filters?.map((f) => f.rowId) ?? [];

/**
 * Returns true if every source filter (matched by filterValue) exists in the target submodule.
 */
export const isPasteAllowed = (
  sourceFilters: AppAccessFilter[],
  targetSubmoduleId: string,
  filterRegistry: SubmoduleFilterRegistry,
): boolean => {
  const targetFilterMap = filterRegistry.get(targetSubmoduleId);
  if (!targetFilterMap) return false;

  const targetFilterValues = new Set(
    Object.values(targetFilterMap).flatMap((filters) => filters.map((f) => f.filterValue)),
  );

  return sourceFilters.every((f) => targetFilterValues.has(f.filterValue));
};

/**
 * Translates source filters to the equivalent entries in the target submodule,
 * matching by filterValue (game ID) since row UUIDs differ per submodule.
 */
export const translateFilters = (
  sourceFilters: AppAccessFilter[],
  targetSubmoduleId: string,
  filterRegistry: SubmoduleFilterRegistry,
): AppAccessFilter[] => {
  const targetFilterMap = filterRegistry.get(targetSubmoduleId);
  if (!targetFilterMap) return [];

  const targetByValue = new Map<string, SubmoduleFilter>();
  Object.values(targetFilterMap).forEach((filters) =>
    filters.forEach((f) => targetByValue.set(f.filterValue, f)),
  );

  return sourceFilters.reduce<AppAccessFilter[]>((acc, source) => {
    const target = targetByValue.get(source.filterValue);
    if (target) {
      acc.push({
        rowId: target.id,
        submoduleFilterId: target.submoduleFilterId,
        filterValue: target.filterValue,
      });
    }
    return acc;
  }, []);
};

export const buildFilterMap = (filters: SubmoduleFilter[]): SubmoduleFilterMap => {
  const map: SubmoduleFilterMap = {};
  for (const f of filters) {
    (map[f.submoduleFilterName] ??= []).push(f);
  }
  return map;
};

export const mapSubmoduleFiltersToFilterItems = (
  filterMap: SubmoduleFilterMap,
): FilterListItemMap => {
  const result: FilterListItemMap = {};
  for (const [key, filters] of Object.entries(filterMap)) {
    result[key] = filters.map(
      (f): FilterListItem => ({
        id: f.id,
        value: f.filterValue,
        label: f.description,
        status: f.gameFundedStatus,
        meta: {
          submoduleFilterId: f.submoduleFilterId,
          filterValue: f.filterValue,
        },
      }),
    );
  }
  return result;
};

export const mapBasicGameDetailsToFilterItems = (
  games: BasicGameApiResponse[],
): FilterListItemMap => ({
  game_id: games.map(
    (g): FilterListItem => ({
      id: g.gameId,
      value: g.gameId,
      label: g.gameName,
      meta: { gameId: g.gameId },
    }),
  ),
});

export const mapBasicCompanyDetailsToFilterItems = (
  companies: BasicCompanyDetails[],
): FilterListItemMap => ({
  client_code: companies.map(
    (c): FilterListItem => ({
      id: c.clientCode,
      value: c.clientCode,
      label: c.name,
    }),
  ),
});

type RoleOption = { value: UserRole; label: string };

export const ALL_ROLE_OPTIONS: RoleOption[] = [
  { value: 'VIEWER', label: 'Viewer' },
  { value: 'ADMIN', label: 'Admin' },
];

/**
 * Returns the set of role options available for a given email + company combination.
 * Both the email domain AND the company name must appear in the env-var restricted lists
 * to unlock the ADMIN option; otherwise only VIEWER is shown.
 */
export const getFilteredRoleOptions = (
  email: string,
  companyId: string,
  companyList: Company[],
): RoleOption[] => {
  const adminEmailDomains = process.env.REACT_APP_ADMIN_EMAIL_DOMAIN_LIST?.split(',') ?? [];
  const adminCompanyNames = process.env.REACT_APP_ADMIN_COMPANY_LIST?.split(',') ?? [];

  const emailDomain = email.split('@')[1] ?? '';
  const isRestrictedEmailDomain = adminEmailDomains.some(
    (domain) => domain.trim().toLowerCase() === emailDomain.toLowerCase(),
  );

  const selectedCompany = companyList.find((c) => c.id === companyId);
  const isRestrictedCompany =
    Boolean(selectedCompany) &&
    adminCompanyNames.some(
      (name) => name.trim().toLowerCase() === selectedCompany!.name.toLowerCase(),
    );

  return isRestrictedEmailDomain && isRestrictedCompany
    ? ALL_ROLE_OPTIONS
    : ALL_ROLE_OPTIONS.filter((o) => o.value !== 'ADMIN');
};

/**
 * Returns the demo game filter ID for a given submodule.
 */
export const getDemoGameFilterId = (
  selectedSubmoduleFilters: SubmoduleFilterMap,
): AppAccessFilter | undefined => {
  const gameIdFilterGroup = selectedSubmoduleFilters?.['game_id'];
  if (!gameIdFilterGroup) return;
  const demoGameFilter = gameIdFilterGroup.find(
    (f) => f.filterValue === process.env.REACT_APP_DEMO_GAME_ID,
  );
  if (!demoGameFilter) return;
  return {
    rowId: demoGameFilter.id,
    submoduleFilterId: demoGameFilter.submoduleFilterId,
    filterValue: demoGameFilter.filterValue,
  };
};

export { buildQueryParams } from '@utils/query';

export const buildSubmodulePayload = (
  submodules: DashboardSelectorSubmoduleItem[],
): UserSubmoduleMap[] =>
  submodules.map((s) => ({
    moduleId: s.moduleId,
    submoduleId: s.submoduleId,
    membershipId: s.membershipId,
    filterIds: toApiFilterIds(s.appAccess?.filters),
    ...(s.appAccess?.type === AppAccessType.FULL_ACCESS && {
      accessType: 'ALL',
      scopeType: SubmoduleScopeType.GLOBAL,
    }),
  }));
